import { useState } from "react";

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import { grey } from '@mui/material/colors';

import { withTranslation } from "react-i18next";

import authService from "@plannel/services/system/auth-service";
import userService from "@plannel/services/system/user-service";
import AuthAppSettingModal from "@plannel/components/modal/AuthAppSettingModal";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import StyledSwitch from "@plannel/components/ui/StyledSwitch"
import AppCard, { CARD_HEADER_SX, SECTION_TITLE_SX } from "@plannel/components/ui/AppCard";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const Authentication = ({ t }) => {
  const currentUser = authService.getCurrentUser();
  const [usingMfa, setUsingMfa] = useState(currentUser.mfa);

  const [authAppModalOpen, setAuthAppModalOpen] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const { snackBarMessageColor } = ColorUtil;

  const updateUserMfa = mfaFlg => {
    const params = { username: currentUser.username, mfaFlg };
    userService.updateUserMfa(params)
      .then(() => {
        const userData = JSON.parse(localStorage.getItem("user"));
        userData.mfa = mfaFlg;
        localStorage.setItem("user", JSON.stringify(userData));

        setUsingMfa(mfaFlg);
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      });
  }

  const handleDialog = (action, _falseAction) => {
    if (action === "MFA") {
      updateUserMfa(true);
      setAuthAppModalOpen(true);
    }
  };

  const handleSwitch = e => {
    const { checked } = e.target;

    if (checked) {
      setDialogInfo({ open: true, title: "twoFactorAuth", actionName: "MFA", content: t("MSG_ConfirmTwoFactorAuth") });
    } else {
      updateUserMfa(checked);
    }
  }

  // MFA 미사용 시 방식(Email/Auth App)을 흐리게 — 비활성 상태 표시
  const methodTitleSx = { ...CARD_HEADER_SX, color: usingMfa ? "text.primary" : grey[500] };

  return (
    <>
      <Box id="security-settings" sx={{ height: "100%", overflow: "auto", px: 3, py: 2 }}>
        <Typography sx={{ fontWeight: 700 }} variant="h5">{t("authentication")}</Typography>
        <Divider sx={{ borderColor: "primary.main", borderBottomWidth: 2, mt: 1, mb: 3 }} />

        <Box sx={{ maxWidth: 720 }}>
          <AppCard>
            <CardContent sx={{ p: 2.5 }}>
              {/* 제목 + 2단계 인증 토글 */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ ...SECTION_TITLE_SX }}>{t("twoFactorAuth")}</Typography>
                <StyledSwitch size="small" name="usingMfa" checked={usingMfa} onChange={handleSwitch} />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.7 }}>
                {t("MSG_AdditionalLayerOfSecurity")} {t("MSG_WithTwoFactorAuth")}
              </Typography>

              <Divider sx={{ my: 2.5 }} />

              {/* Email OTP */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, py: 1 }}>
                <Box>
                  <Typography sx={methodTitleSx}>{t("email")}</Typography>
                  <Typography variant="body2" sx={{ color: grey[500], mt: 0.25 }}>{t("sendOtp")}</Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              {/* Auth App */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, py: 1 }}>
                <Box>
                  <Typography sx={methodTitleSx}>{t("authApp")}</Typography>
                  <Typography variant="body2" sx={{ color: grey[500], mt: 0.25 }}>{t("googleAuthApp")}</Typography>
                </Box>
                <Button size="medium" color="primary" variant="outlined" disableElevation disabled={!usingMfa}
                  onClick={() => setAuthAppModalOpen(true)}>{t("settings")}</Button>
              </Box>
            </CardContent>
          </AppCard>
        </Box>
      </Box>
      <AuthAppSettingModal
        open={authAppModalOpen}
        setOpen={setAuthAppModalOpen}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        actionName={dialogInfo.actionName}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(Authentication);
